import { useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/**
 * ImageUpload: accepts either a URL string or a File converted to base64.
 * Stores result as string in parent state.
 */
export default function ImageUpload({ value, onChange, label = "Image", accept = "image/*", testId }) {
  const [loading, setLoading] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2 MB. Please compress and try again.");
      return;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result);
      setLoading(false);
    };
    reader.onerror = () => {
      toast.error("Could not read the file.");
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs tracking-wider uppercase text-foreground/70">{label}</p>
      <div className="flex items-start gap-3">
        {value ? (
          <div className="relative h-20 w-20 rounded-sm overflow-hidden border border-border/60 shrink-0">
            <img src={value} alt="preview" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center"
            >
              <X size={11} />
            </button>
          </div>
        ) : (
          <div className="h-20 w-20 rounded-sm border border-dashed border-border flex items-center justify-center text-foreground/40 shrink-0">
            <Upload size={16} />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <Input
            placeholder="Paste image URL (https://...)"
            value={value?.startsWith("data:") ? "[uploaded file]" : (value || "")}
            onChange={(e) => onChange(e.target.value)}
            disabled={value?.startsWith("data:")}
            className="h-9 rounded-sm bg-card"
            data-testid={testId}
          />
          <label className="inline-flex">
            <input
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button asChild variant="outline" size="sm" className="h-9 rounded-sm border-gold/40 hover:border-gold text-xs tracking-wider uppercase cursor-pointer">
              <span>{loading ? <><Loader2 size={12} className="animate-spin mr-2" /> Uploading…</> : <><Upload size={12} className="mr-2" /> Upload File</>}</span>
            </Button>
          </label>
        </div>
      </div>
    </div>
  );
}
